import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getConfig } from '../lib/config';
import { loadCredentials, saveCredentials, clearCredentials } from '../lib/credentials';
import { opsApi } from '../lib/ops-api';
import type { Credentials } from '../lib/types';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  email: string;
  apiKey: string;
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthState>({
  isAuthenticated: false,
  isLoading: true,
  email: '',
  apiKey: '',
  login: async () => 'Not initialized',
  logout: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [apiKey, setApiKey] = useState('');

  // Restore saved credentials on mount
  useEffect(() => {
    const saved = loadCredentials();
    if (saved) {
      setEmail(saved.email);
      setApiKey(saved.apiKey);
      opsApi.setApiKey(saved.apiKey);
      setIsAuthenticated(true);
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<string | null> => {
    const config = getConfig();

    // Mock mode — accept any credentials
    if (config.isMock) {
      const mockKey = 'kc-mock-local-dev';
      opsApi.setApiKey(mockKey);
      const credentials: Credentials = { apiKey: mockKey, email };
      saveCredentials(credentials);
      setEmail(email);
      setApiKey(mockKey);
      setIsAuthenticated(true);
      return null;
    }

    try {
      const response = await fetch(`${config.authApiUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        return body.error || `Login failed (${response.status})`;
      }

      const data = await response.json();
      const key = data.api_key;

      if (!key) {
        return 'No API key in login response';
      }

      // Verify the key works with the ops API
      opsApi.setApiKey(key);
      try {
        await opsApi.getOverview();
      } catch (err) {
        opsApi.setApiKey('');
        return err instanceof Error ? err.message : 'Ops API access denied';
      }

      // Success - save and update state
      const credentials: Credentials = { apiKey: key, email };
      saveCredentials(credentials);
      setEmail(email);
      setApiKey(key);
      setIsAuthenticated(true);

      return null; // No error
    } catch (err) {
      return err instanceof Error ? err.message : 'Network error';
    }
  }, []);

  const logout = useCallback(() => {
    clearCredentials();
    opsApi.setApiKey('');
    setIsAuthenticated(false);
    setEmail('');
    setApiKey('');
  }, []);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, email, apiKey, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
